/**
 * v2.9.150 — Shazam Pipeline Indicator
 * ------------------------------------------------------------------
 * Top-level summary widget showing how many active stations use
 * Shazam, with a tooltip breakdown of mandatory vs fallback.
 *
 * Rendered at the top of the Stream Monitor Health Dashboard (next
 * to the existing fleet status pills).
 *
 * Data source: GET /api/admin/diagnostic/shazam-active-stations
 *   • total           — count of all stations using Shazam
 *   • mandatory_count — Shazam-only stations (blue)
 *   • fallback_count  — auto-mode stations that ran Shazam at least
 *                       once in this pod's lifetime (gray)
 *
 * The component auto-refreshes every 60s (same cadence as the
 * health dashboard). Failure to fetch (network / 401 / 5xx) hides
 * the indicator silently — it's a nice-to-have, not critical UX.
 *
 * Click → toggles an expanded panel listing all the Shazam
 * stations with their mode + last attempt timestamp (when the
 * `mode` field is present in the response).
 *
 * Props:
 *   • token: JWT for the admin API
 *   • className: optional wrapper classes
 */
import React, { useEffect, useState, useCallback } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { ShazamBadge } from './ShazamBadge';

const API = process.env.REACT_APP_BACKEND_URL;

export const ShazamPipelineIndicator = ({ token, className = '' }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(
        `${API}/api/admin/diagnostic/shazam-active-stations`,
        { headers }
      );
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch (e) {
      // silent — non-critical widget
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!cancelled) await fetchData();
    };
    run();
    const id = setInterval(run, 60000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [fetchData]);

  if (loading || !data || data.total === 0) {
    return null; // Don't render anything until we have data
  }

  const { total, mandatory_count, fallback_count, mandatory = [], fallback = [] } = data;
  const tooltipText = `${total} stations using Shazam: ${mandatory_count} Shazam-only, ${fallback_count} auto fallback`;

  return (
    <div
      data-testid="shazam-pipeline-indicator"
      className={`inline-block ${className}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        title={tooltipText}
        data-testid="shazam-pipeline-toggle"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-blue-500/10 to-gray-500/10 border border-blue-400/30 hover:border-blue-400/60 text-blue-700 dark:text-blue-300 text-xs font-medium transition-colors"
      >
        <ShazamBadge mode="mandatory" size="sm" />
        <span>
          {total} σταθμοί με Shazam
        </span>
        <span className="opacity-70">
          ({mandatory_count} only · {fallback_count} fallback)
        </span>
        {expanded ? (
          <ChevronUp className="w-3 h-3" />
        ) : (
          <ChevronDown className="w-3 h-3" />
        )}
      </button>

      {expanded && (
        <div
          data-testid="shazam-pipeline-breakdown"
          className="mt-2 w-80 max-h-96 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl p-3 space-y-3 text-xs"
        >
          {mandatory_count > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-1.5 text-blue-700 dark:text-blue-300 font-semibold">
                <ShazamBadge mode="mandatory" size="sm" />
                <span>Shazam-only · {mandatory_count}</span>
              </div>
              <ul className="space-y-0.5 pl-2 text-gray-700 dark:text-gray-300">
                {mandatory.map((s) => (
                  <li
                    key={s.station_id}
                    data-testid={`shazam-mandatory-station-${s.station_id}`}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="truncate">{s.name}</span>
                    {s.has_telemetry_samples && (
                      <span
                        title="Έχει τρέξει τουλάχιστον ένα Shazam attempt"
                        className="text-[10px] text-emerald-600 dark:text-emerald-400 flex-shrink-0"
                      >
                        ●
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {fallback_count > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-1.5 text-gray-600 dark:text-gray-400 font-semibold">
                <ShazamBadge mode="fallback" size="sm" />
                <span>Auto fallback · {fallback_count}</span>
              </div>
              <ul className="space-y-0.5 pl-2 text-gray-600 dark:text-gray-400">
                {fallback.map((s) => (
                  <li
                    key={s.station_id}
                    data-testid={`shazam-fallback-station-${s.station_id}`}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="truncate">{s.name}</span>
                    <span className="text-[10px] flex-shrink-0">
                      {s.real_attempts || 0}×
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ShazamPipelineIndicator;
